import { useCallback, useEffect, useMemo, useState } from "react";
import { Pencil } from "lucide-react";
import { ThreeDots } from "react-loader-spinner";

import { displayFormatDate } from "../../../../../../utils/helpers.js";
import { EmptyStateMessage } from "../../../../shared/empty-state-message/empty-state-message.tsx";
import {
  Badge,
  Button,
  ResponsiveDataTable,
  TextField,
  type ResponsiveColumn,
} from "../../../../ui/index.ts";
import { showError, showSuccess } from "../../../../../utils/toast.ts";
import { DEFAULT_PAGE_SIZE } from "../../constants.ts";
import { useDepartments } from "../../hooks/use-departments.ts";
import { DepartmentFormModal } from "../../modals/department-form-modal/department-form-modal.tsx";
import type { DepartmentRow } from "../../types.ts";
import {
  formatStatusLabel,
  statusBadgeVariant,
} from "../../utils/format-status-badge.ts";
import styles from "./departments-tab.module.css";

type DepartmentTableRow = Omit<DepartmentRow, "id"> & {
  id: string;
} & Record<string, unknown>;

export interface DepartmentsTabProps {
  onDepartmentsChanged?: () => void;
}

export function DepartmentsTab({ onDepartmentsChanged }: DepartmentsTabProps) {
  const {
    departments,
    loading,
    submitting,
    error,
    createDepartment,
    updateDepartment,
  } = useDepartments();

  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDepartment, setSelectedDepartment] =
    useState<DepartmentRow | null>(null);

  useEffect(() => {
    if (error) showError(error);
  }, [error]);

  const filteredDepartments = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return departments;
    return departments.filter((row) =>
      row.name.toLowerCase().includes(query),
    );
  }, [departments, searchTerm]);

  const paginatedDepartments = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredDepartments.slice(start, start + pageSize);
  }, [filteredDepartments, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, pageSize]);

  const tableRows: DepartmentTableRow[] = useMemo(
    () =>
      paginatedDepartments.map((row) => ({
        ...row,
        id: String(row.id),
      })),
    [paginatedDepartments],
  );

  const openCreateModal = () => {
    setSelectedDepartment(null);
    setModalOpen(true);
  };

  const openEditModal = useCallback((row: DepartmentTableRow) => {
    setSelectedDepartment({
      ...row,
      id: Number(row.id),
    });
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setSelectedDepartment(null);
  };

  const handleSubmit = async (name: string) => {
    const result = selectedDepartment
      ? await updateDepartment(selectedDepartment.id, name)
      : await createDepartment(name);

    if (result.ok) {
      if (result.message) showSuccess(result.message);
      onDepartmentsChanged?.();
      return true;
    }
    if (result.message) showError(result.message);
    return false;
  };

  const handlePageSizeChange = useCallback((next: number) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const columns = useMemo<ResponsiveColumn<DepartmentTableRow>[]>(
    () => [
      {
        id: "name",
        header: "Department Name",
        accessor: "name",
        sortable: true,
        truncate: true,
      },
      {
        id: "creationDate",
        header: "Creation Date",
        sortable: true,
        nowrap: true,
        getSortValue: (row) => row.creationDate ?? "",
        render: (row) => displayFormatDate(row.creationDate ?? ""),
        getTitleText: (row) => displayFormatDate(row.creationDate ?? ""),
      },
      {
        id: "status",
        header: "Status",
        sortable: true,
        nowrap: true,
        accessor: "status",
        render: (row) => (
          <Badge variant={statusBadgeVariant(row.status)}>
            {formatStatusLabel(row.status)}
          </Badge>
        ),
      },
      {
        id: "actions",
        header: "Actions",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className={styles.iconBtn}
            aria-label={`Edit ${row.name}`}
            onClick={() => openEditModal(row)}
          >
            <Pencil size={16} aria-hidden />
          </button>
        ),
      },
    ],
    [openEditModal],
  );

  return (
    <div>
      <div className={styles.toolbar}>
        <div className={styles.searchWrap}>
          <TextField
            id="departments-search"
            label="Department Name"
            placeholder="Search Department Name"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className={styles.actions}>
          <Button variant="accent" onClick={openCreateModal}>
            + Add Department
          </Button>
        </div>
      </div>

      {loading ? (
        <div className={styles.loadingWrap}>
          <ThreeDots
            height="60"
            width="60"
            color="var(--color-accent-500)"
            visible
          />
        </div>
      ) : (
        <div className={styles.tableWrap}>
          <ResponsiveDataTable<DepartmentTableRow>
            serialNumber
            columns={columns}
            data={tableRows}
            defaultSortKey="name"
            defaultSortDir="asc"
            aria-label="Departments"
            emptyState={
              <EmptyStateMessage message="No departments found." />
            }
            pagination
            page={page}
            pageSize={pageSize}
            totalItems={filteredDepartments.length}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={[5, 10, 20, 30, 40]}
          />
        </div>
      )}

      <DepartmentFormModal
        open={modalOpen}
        department={selectedDepartment}
        submitting={submitting}
        onClose={closeModal}
        onSubmit={handleSubmit}
      />
    </div>
  );
}
